import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
  ArrowLeft,
  CheckCircle,
  AlertTriangle,
  Brain,
  Award,
  Target,
  Heart,
  Lightbulb,
  Star,
  TrendingUp,
} from "lucide-react";
import Layout from "../../layout";
import { useAuth } from "../../authContext";
import { wellbeingLevels } from "./wellbeingAssessmentData";

const WellbeingAssessmentDetail = ({ onBack, onComplete }) => {
  const { url, headers } = useAuth();
  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [results, setResults] = useState(null);
  const [showResults, setShowResults] = useState(false);
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch questions from API
  useEffect(() => {
    const fetchQuestions = async () => {
      try {
        setLoading(true);
        const response = await fetch(
          `${url}/assessment/questions?testtype=wellness`,
          {
            method: "GET",
            headers: headers,
          }
        );

        if (response.ok) {
          const apiResponse = await response.json();
          const data = apiResponse.data; 
          const transformedQuestions = data.questions.map((q) => ({
            id: q.id,
            category: q.category,
            question: q.question,
            options: [
              {
                text: q.option1_text,
                score: Number(q.option1_score) || 0,
                explanation: q.option1_explanation,
              },
              {
                text: q.option2_text,
                score: Number(q.option2_score) || 0,
                explanation: q.option2_explanation,
              },
              {
                text: q.option3_text,
                score: Number(q.option3_score) || 0,
                explanation: q.option3_explanation,
              },
              {
                text: q.option4_text,
                score: Number(q.option4_score) || 0,
                explanation: q.option4_explanation,
              },
              {
                text: q.option5_text,
                score: Number(q.option5_score) || 0,
                explanation: q.option5_explanation,
              },
            ].filter((opt) => opt.text && opt.text.trim() !== ""), // Remove empty options
          }));

          setQuestions(transformedQuestions);
          //console.log('✅ Wellbeing questions fetched successfully:', transformedQuestions.length);
        } else {
          throw new Error(`HTTP ${response.status}`);
        }
      } catch (error) {
        console.error("❌ Error fetching wellbeing questions:", error);
        setError("Failed to load questions. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    fetchQuestions();
  }, [url, headers]);

  const handleAnswer = (score) => {
    const newAnswers = [...answers, score];
    setAnswers(newAnswers);

    if (currentStep < questions.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      calculateResults(newAnswers);
    }
  };

  const calculateResults = (allAnswers) => {
    const totalScore = allAnswers.reduce((sum, score) => sum + score, 0);
    const maxScore = questions.length * 4; // 0-4 scale
    const percentage = Math.round((totalScore / maxScore) * 100);

    let wellbeingLevel;
    if (totalScore >= 0 && totalScore <= 16) {
      wellbeingLevel = "low";
    } else if (totalScore >= 17 && totalScore <= 28) {
      wellbeingLevel = "moderate";
    } else if (totalScore >= 29 && totalScore <= 36) {
      wellbeingLevel = "good";
    } else {
      wellbeingLevel = "high";
    }

    const levelData = wellbeingLevels[wellbeingLevel];

    const categoryScores = {};
    const categories = [
      "Positive Emotions",
      "Engagement (Flow)",
      "Relationships",
      "Meaning & Purpose",
      "Accomplishment",
      "Physical Health",
      "Emotional Regulation",
      "Life Satisfaction",
      "Personal Growth",
      "Self-Acceptance",
    ];

    categories.forEach((category, index) => {
      categoryScores[category] = allAnswers[index] || 0;
    });

    // Calculate PERMA+ domain averages
    const permaScores = {
      "Positive Emotions": allAnswers[0] || 0,
      Engagement: allAnswers[1] || 0,
      Relationships: allAnswers[2] || 0,
      Meaning: allAnswers[3] || 0,
      Accomplishment: allAnswers[4] || 0,
      Health: ((allAnswers[5] || 0) + (allAnswers[6] || 0)) / 2, // Physical + Emotional
      "Life Satisfaction": allAnswers[7] || 0,
      "Personal Growth": allAnswers[8] || 0,
      "Self-Acceptance": allAnswers[9] || 0,
    };

    const resultData = {
      score: totalScore,
      maxScore,
      percentage,
      level: levelData.level,
      message: levelData.description,
      recommendations: levelData.recommendations,
      color: levelData.color,
      icon: levelData.icon,
      emergencyLevel: levelData.emergencyLevel,
      categoryScores,
      permaScores,
      completedAt: new Date().toISOString(),
    };

    setResults(resultData);
    setShowResults(true);
    onComplete(resultData);
  };

  const resetAssessment = () => {
    setCurrentStep(0);
    setAnswers([]);
    setResults(null);
    setShowResults(false);
  };

  const progress =
    questions.length > 0 ? ((currentStep + 1) / questions.length) * 100 : 0;

  if (loading) {
    return (
      <Layout>
        <div className="min-h-screen p-4 sm:p-6 flex items-center justify-center">
          <div className="text-center">
            <div className="text-6xl mb-4">🌟</div>
            <div className="text-xl font-semibold text-gray-800 mb-2">
              Loading Wellbeing Assessment...
            </div>
            <div className="text-gray-600">Preparing your questions</div>
          </div>
        </div>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <div className="min-h-screen p-4 sm:p-6 flex items-center justify-center">
          <div className="text-center">
            <div className="text-6xl mb-4">❌</div>
            <div className="text-xl font-semibold text-red-600 mb-2">
              Error Loading Questions
            </div>
            <div className="text-gray-600 mb-4">{error}</div>
            <button
              onClick={onBack}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Go Back
            </button>
          </div>
        </div>
      </Layout>
    );
  }

  if (questions.length === 0) {
    return (
      <Layout>
        <div className="min-h-screen p-4 sm:p-6 flex items-center justify-center">
          <div className="text-center">
            <div className="text-6xl mb-4">📝</div>
            <div className="text-xl font-semibold text-gray-800 mb-2">
              No Questions Available
            </div>
            <div className="text-gray-600 mb-4">Please try again later</div>
            <button
              onClick={onBack}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Go Back
            </button>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="min-h-screen p-4 sm:p-6">
        <div className="max-w-8xl mx-auto">
          <motion.button
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            onClick={onBack}
            className="flex items-center gap-2 text-blue-600 hover:text-blue-700 mb-4 sm:mb-6 font-medium text-sm sm:text-base"
          >
            <ArrowLeft className="w-4 h-4 sm:w-5 sm:h-5" />
            Back to Mental Health Hub
          </motion.button>

          {!showResults ? (
            <>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-gradient-to-r from-green-600 to-emerald-600 rounded-xl sm:rounded-2xl p-4 sm:p-6 lg:p-8 text-white mb-6 sm:mb-8"
              >
                <div className="flex flex-col sm:flex-row items-center gap-3 sm:gap-4 lg:gap-6">
                  <div className="text-5xl sm:text-6xl lg:text-7xl">🌟</div>
                  <div className="text-center sm:text-left">
                    <h1 className="text-xl sm:text-2xl lg:text-3xl xl:text-4xl font-bold mb-1 sm:mb-2">
                      Wellbeing Assessment (PERMA+)
                    </h1>
                    <p className="text-sm sm:text-base lg:text-lg xl:text-xl text-green-100 mb-1 sm:mb-2">
                      Evaluate your psychological wellbeing across key life
                      dimensions
                    </p>
                    <div className="bg-white/20 px-3 sm:px-4 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm font-medium inline-block">
                      Question {currentStep + 1} of {questions.length}
                    </div>
                  </div>
                </div>

                <div className="w-full bg-white/20 rounded-full h-2 sm:h-3 mt-4 sm:mt-6">
                  <div
                    className="bg-white h-2 sm:h-3 rounded-full transition-all duration-300"
                    style={{ width: `${progress}%` }}
                  ></div>
                </div>
              </motion.div>

              <motion.div
                key={currentStep}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                className="bg-white rounded-lg sm:rounded-xl shadow-lg p-4 sm:p-6 lg:p-8"
              >
                <div className="mb-4">
                  <span className="inline-block px-3 py-1 bg-green-100 text-green-800 text-sm font-medium rounded-full">
                    {questions[currentStep].category}
                  </span>
                </div>

                <h2 className="text-lg sm:text-xl lg:text-2xl font-semibold mb-4 sm:mb-6 text-gray-900">
                  {questions[currentStep].question}
                </h2>

                <div className="space-y-3 sm:space-y-4">
                  {questions[currentStep].options.map((option, index) => (
                    <button
                      key={index}
                      onClick={() => handleAnswer(option.score)}
                      className="w-full text-left p-3 sm:p-4 lg:p-6 rounded-lg border-2 border-gray-200 hover:border-green-300 hover:bg-green-50 transition-all text-sm sm:text-base group"
                    >
                      <div className="flex items-center gap-3 sm:gap-4">
                        <div className="w-5 h-5 sm:w-6 sm:h-6 border-2 border-gray-400 group-hover:border-green-500 rounded-full flex-shrink-0 transition-colors"></div>
                        <div className="flex-1">
                          <span className="block font-medium">
                            {option.text}
                          </span>
                          {option.explanation && (
                            <span className="text-xs text-gray-500 mt-1 block">
                              {option.explanation}
                            </span>
                          )}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>

                <div className="flex justify-between items-center mt-6 sm:mt-8">
                  <button
                    onClick={() => setCurrentStep(Math.max(0, currentStep - 1))}
                    disabled={currentStep === 0}
                    className="px-4 sm:px-6 py-2 sm:py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base"
                  >
                    Previous
                  </button>
                  <div className="text-xs sm:text-sm text-gray-500">
                    {currentStep + 1} of {questions.length}
                  </div>
                </div>
              </motion.div>
            </>
          ) : (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="space-y-6 sm:space-y-8"
            >
              {results.emergencyLevel && (
                <div className="bg-red-50 border-2 border-red-200 rounded-lg sm:rounded-xl p-4 sm:p-6">
                  <div className="flex items-start gap-2 sm:gap-3">
                    <AlertTriangle className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <h3 className="font-bold text-red-800 mb-2 text-lg">
                        ⚠️ High Anxiety Level Detected
                      </h3>
                      <p className="text-red-700 text-sm sm:text-base mb-4">
                        Your anxiety levels indicate you may need professional
                        support. Please consider reaching out to a CubeCover
                        Mental Health Therapist for immediate assistance.
                      </p>
                      <a
                        href="https://wa.me/2349160001429?text=Hi, I just completed an anxiety assessment and my results indicate I may need support. Can you help me?"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center justify-center gap-2 px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm sm:text-base font-medium"
                      >
                        <svg
                          className="w-5 h-5"
                          fill="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488" />
                        </svg>
                        Contact via WhatsApp
                      </a>
                    </div>
                  </div>
                </div>
              )}

              <div
                className={`bg-gradient-to-r ${results.color} rounded-xl sm:rounded-2xl p-4 sm:p-6 lg:p-8 text-white text-center`}
              >
                <div className="text-5xl sm:text-6xl lg:text-7xl mb-4">
                  {results.icon}
                </div>
                <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold mb-2">
                  Assessment Complete!
                </h1>
                <p className="text-sm sm:text-base lg:text-lg opacity-90 mb-4">
                  Your Wellbeing Results
                </p>
                <div className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-2">
                  {results.score}/40
                </div>
                <div className="text-lg sm:text-xl lg:text-2xl font-semibold">
                  {results.level}
                </div>
              </div>

              <div className="bg-white rounded-lg sm:rounded-xl shadow-lg p-4 sm:p-6 lg:p-8">
                <h2 className="text-lg sm:text-xl lg:text-2xl font-semibold mb-4 sm:mb-6 flex items-center gap-2 sm:gap-3">
                  <Brain className="w-5 h-5 sm:w-6 sm:h-6 text-green-600" />
                  Understanding Your Wellbeing
                </h2>
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 sm:p-6 mb-4 sm:mb-6">
                  <p className="text-green-800 text-sm sm:text-base leading-relaxed">
                    {results.message}
                  </p>
                </div>

                <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <Target className="w-5 h-5 text-blue-600" />
                  Recommended Actions
                </h3>
                <div className="space-y-3 sm:space-y-4">
                  {results.recommendations.map((rec, index) => (
                    <div
                      key={index}
                      className="flex items-start gap-2 sm:gap-3 p-3 sm:p-4 bg-blue-50 rounded-lg"
                    >
                      <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                      <span className="text-sm sm:text-base text-gray-800">
                        {rec}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                <button
                  onClick={resetAssessment}
                  className="flex-1 py-2 sm:py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm sm:text-base"
                >
                  Take Assessment Again
                </button>
                <button
                  onClick={onBack}
                  className="flex-1 py-2 sm:py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm sm:text-base"
                >
                  Back to Mental Health Hub
                </button>
              </div>
            </motion.div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default WellbeingAssessmentDetail;
